import { useEffect, useState } from "react";
import { Copy, Trash2, Plus, RefreshCw, Link as LinkIcon, ShieldCheck, Mail, MessageSquare, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, type AppRole } from "@/lib/auth-context";
import { useTenantOverride } from "@/lib/tenant-override";
import { useEffectiveBrand } from "@/lib/tenant-branding";
import { openEmailInvite, openSmsInvite } from "@/lib/invite-templates";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

type InviteRow = {
  id: string; email: string; role: AppRole;
  token: string; tenant_id: string; school_id: string | null;
  expires_at: string; accepted_at: string | null; revoked_at: string | null;
  metadata: { phone?: string } | null;
  created_at: string;
};

const ROLES: AppRole[] = ["parent", "student", "school_admin", "merchant"];

function randomToken(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

export function InvitationsPanel({ schoolId }: { schoolId?: string | null } = {}) {
  const { user, isSuperadmin } = useAuth();
  const { activeTenantId, activeTenant } = useTenantOverride();
  const { brand } = useEffectiveBrand();
  const [rows, setRows] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<AppRole>("parent");
  const [days, setDays] = useState(7);
  const [saving, setSaving] = useState(false);

  async function load() {
    if (!activeTenantId) return;
    setLoading(true);
    const { data, error } = await (supabase.from("tenant_invitations") as any)
      .select("id,email,role,token,tenant_id,school_id,expires_at,accepted_at,revoked_at,metadata,created_at")
      .eq("tenant_id", activeTenantId)
      .order("created_at", { ascending: false })
      .limit(200);
    setLoading(false);
    if (error) return toast.error("Failed to load invites", { description: error.message });
    setRows((data as InviteRow[] | null) ?? []);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTenantId]);

  async function create(mode: "link" | "email" | "sms" = "link") {
    if (!email.trim() || !activeTenantId || !user) return;
    setSaving(true);
    const token = randomToken();
    const expires_at = new Date(Date.now() + days * 86400_000).toISOString();
    const { data, error } = await (supabase.from("tenant_invitations") as any).insert({
      tenant_id: activeTenantId,
      email: email.trim().toLowerCase(),
      role, school_id: schoolId ?? null,
      token, expires_at, invited_by: user.id,
      metadata: { phone: phone.trim() || null, delivery: mode },
    }).select("id,email,role,token,tenant_id,school_id,expires_at,accepted_at,revoked_at,metadata,created_at").single();
    setSaving(false);
    if (error) return toast.error("Could not create invite", { description: error.message });
    const row = data as InviteRow;
    const payload = {
      tenantName: activeTenant?.name ?? brand.name ?? "this tenant",
      logoUrl: brand.logoUrl,
      primaryColor: brand.primaryColor,
      accentColor: brand.accentColor,
      supportEmail: brand.supportEmail,
      footerText:
        brand.footerText?.trim() ||
        (brand.tenantSlug === "fundred" || activeTenant?.slug === "fundred"
          ? "Powered by TriSphere · A Trisphere Group (U) Ltd Platform"
          : "Powered by TriSphere"),
      role,
      email: row.email,
      inviteUrl: inviteUrl(row.token),
      expiresAt: row.expires_at,
    };
    if (mode === "email") openEmailInvite(payload);
    if (mode === "sms") openSmsInvite(payload, phone);
    setEmail("");
    setPhone("");
    toast(mode === "link" ? "Invite created" : "Invite ready to send", { description: `${role} · expires in ${days} days` });
    load();
  }

  async function revoke(id: string) {
    const { error } = await (supabase.from("tenant_invitations") as any).update({ revoked_at: new Date().toISOString() }).eq("id", id);
    if (error) return toast.error("Revoke failed", { description: error.message });
    toast("Invite revoked");
    load();
  }

  function inviteUrl(token: string): string {
    const slug = activeTenant?.slug ?? brand.tenantSlug ?? "tricampus";
    // Prefer tenant-specific host if we're already on it; otherwise fall back to current origin
    const host = typeof window !== "undefined" ? window.location.origin : "";
    const preferSlugHost = slug !== "tricampus" && host && !host.includes(`${slug}.`);
    const base = preferSlugHost ? `https://${slug}.tricampus.live` : host;
    return `${base}/invite/${token}`;
  }

  async function copy(token: string) {
    try { await navigator.clipboard.writeText(inviteUrl(token)); toast("Invite link copied"); }
    catch { toast.error("Copy failed"); }
  }

  const canManage = isSuperadmin || true; // school_admin RLS enforces on server

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Tenant invitations</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Invite Parents, Students, School Admins and Merchants to <b>{activeTenant?.name ?? "this tenant"}</b>. Links are single-use, tenant-locked, and email-verified.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}><RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
      </CardHeader>
      <CardContent className="space-y-5">
        {canManage && (
          <div className="grid grid-cols-1 gap-3 rounded-lg border bg-muted/30 p-3 md:grid-cols-6">
            <div className="md:col-span-2"><Label className="text-xs">Email</Label><Input type="email" placeholder="parent@example.com" value={email} onChange={e => setEmail(e.target.value)} /></div>
            <div><Label className="text-xs">Phone for SMS</Label><Input type="tel" placeholder="+256…" value={phone} onChange={e => setPhone(e.target.value)} /></div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={role} onValueChange={v => setRole(v as AppRole)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r.replace("_"," ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Expires (days)</Label><Input type="number" min={1} max={90} value={days} onChange={e => setDays(Math.max(1, Number(e.target.value) || 7))} /></div>
            <div className="flex items-end gap-1">
              <Button className="flex-1" disabled={saving || !email.trim()} onClick={() => create("email")}><Send className="mr-1 h-3.5 w-3.5" />Send invite</Button>
              <Button variant="outline" disabled={saving || !email.trim()} onClick={() => create("sms")} aria-label="Send SMS invite"><MessageSquare className="h-3.5 w-3.5" /></Button>
              <Button variant="outline" disabled={saving || !email.trim()} onClick={() => create("link")} aria-label="Create invite link"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        )}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-6 text-center text-xs text-muted-foreground">{loading ? "Loading…" : "No invites yet."}</TableCell></TableRow>
              )}
              {rows.map(r => {
                const status = r.revoked_at ? "revoked" : r.accepted_at ? "accepted" : new Date(r.expires_at) < new Date() ? "expired" : "pending";
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.email}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{r.role.replace("_"," ")}</Badge></TableCell>
                    <TableCell>
                      <Badge variant={status === "accepted" ? "default" : status === "pending" ? "secondary" : "destructive"}>{status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{new Date(r.expires_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      {status === "pending" && (
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => copy(r.token)}><LinkIcon className="mr-1 h-3.5 w-3.5" />Copy link</Button>
                          <Button size="sm" variant="ghost" onClick={() => openEmailInvite({ tenantName: activeTenant?.name ?? brand.name ?? "this tenant", logoUrl: brand.logoUrl, primaryColor: brand.primaryColor, accentColor: brand.accentColor, supportEmail: brand.supportEmail, footerText: brand.footerText, role: r.role, email: r.email, inviteUrl: inviteUrl(r.token), expiresAt: r.expires_at })}><Mail className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => openSmsInvite({ tenantName: activeTenant?.name ?? brand.name ?? "this tenant", logoUrl: brand.logoUrl, primaryColor: brand.primaryColor, accentColor: brand.accentColor, supportEmail: brand.supportEmail, footerText: brand.footerText, role: r.role, email: r.email, inviteUrl: inviteUrl(r.token), expiresAt: r.expires_at }, r.metadata?.phone)}><MessageSquare className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => revoke(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                      {status !== "pending" && (
                        <Button size="sm" variant="ghost" onClick={() => copy(r.token)}><Copy className="h-3.5 w-3.5" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
